import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { COURSE_MEASURE_FIELDS } from '@ast-data/shared';
import RepeaterList from '../../components/RepeaterList';
import type { CourseRow } from './courseRows';

// ---------------------------------------------------------------------------
/**
 * The Lagen list editor of a „baulicher Bestand" — a RepeaterList like the
 * Farben, Beimengungen and Schichten before it, but BANDED: a Lage carries eight
 * fields, and as one wrapping row there was no telling where one ended and the
 * next began.
 *
 * This is what REPLACES the flat measure fields on that kind of description: a
 * Mauer or ein Fundament is not one set of figures but a stack of them, and each
 * course carries its own. Planum and Profil show the very same band, which is
 * what makes the two forms identical there.
 *
 * The two heights are NHN readings in metres, everything else a length in
 * centimetres, and the Niv. Blatt is free text — it names a sheet, not a
 * measure. `labelPrefix` is the form's own catalog, so a Profil's Lage is
 * labelled from `profile.field.*` and a Planum's from `planum.field.*`.
 */
// ---------------------------------------------------------------------------

/** Which unit a Lage's measure is stated in. */
const HEIGHT_FIELDS = new Set(['heightTop', 'heightBottom']);

export default function CourseListEditor({
    rows,
    onChange,
    labelPrefix
}: {
    rows: CourseRow[];
    onChange: (rows: CourseRow[]) => void;
    /** `planum.field` or `profile.field` — see the note above. */
    labelPrefix: string;
}) {
    const intl = useIntl();
    // The very same two keys the flat measure fields state their unit with
    // (see the forms' `unitId`) — one source per unit, so a Lage's Breite and
    // a description's Breite can never come to read differently.
    const centimetres = intl.formatMessage({ id: 'common.centimetres' });
    const metres = intl.formatMessage({ id: 'common.metres' });

    /** One measure cell — the unit sits in the field, not in the value. */
    function measure(
        row: CourseRow,
        update: (patch: Partial<CourseRow>) => void,
        key: (typeof COURSE_MEASURE_FIELDS)[number]
    ) {
        const unit = HEIGHT_FIELDS.has(key) ? metres : centimetres;
        return (
            <TextField
                key={key}
                fullWidth
                value={row[key]}
                onChange={(event) => update({ [key]: event.target.value })}
                label={intl.formatMessage({ id: `${labelPrefix}.${key}` })}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                {unit}
                            </InputAdornment>
                        )
                    }
                }}
            />
        );
    }

    return (
        <RepeaterList
            rows={rows}
            onChange={onChange}
            blankRow={{
                name: '',
                length: '',
                width: '',
                depth: '',
                diameter: '',
                heightTop: '',
                heightBottom: '',
                levelSheet: ''
            }}
            labelId={`${labelPrefix}.courses`}
            emptyId="planum.course.empty"
            addId="planum.course.add"
            removeId="planum.course.remove"
            band
            removeAt="header"
            renderHeader={(row, update) => (
                /* SMALL on purpose: the fields below are the theme's default
                   (medium), so the smaller box reads as the band's head rather
                   than as a ninth field — without inventing a type size for it.
                   It starts at the band's own left edge, on the same line every
                   field below it stands on. */
                <TextField
                    fullWidth
                    value={row.name}
                    onChange={(event) => update({ name: event.target.value })}
                    label={intl.formatMessage({
                        id: `${labelPrefix}.courseName`
                    })}
                />
            )}
            renderRow={(row, update) => (
                <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(3, 1fr)'
                            }
                        }}
                    >
                        {COURSE_MEASURE_FIELDS.map((field) =>
                            measure(row, update, field)
                        )}
                    </Box>
                    {/* Its own line: it names a sheet, not a measure, and the
                        six figures above are read as one group. */}
                    <TextField
                        fullWidth
                        value={row.levelSheet}
                        onChange={(event) =>
                            update({ levelSheet: event.target.value })
                        }
                        label={intl.formatMessage({
                            id: `${labelPrefix}.levelSheet`
                        })}
                    />
                </Box>
            )}
        />
    );
}
